import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis,
} from "recharts";
import {
  fetchLeaderboard, fetchRunDetail, promoteTradingModel,
  type ModelPerformance, type RunTrial,
} from "../api/client";

/** Per-run trial explorer: every Phase 1 trial as a row, sortable + filterable.
 *  Scatter plots of (numeric hyperparam → score) per ticker to surface
 *  which hyperparameter regions yielded high scores. */

type SortField =
  | "ticker"
  | "trial_number"
  | "agent_mean_ret_pct"
  | "cagr_pct"
  | "max_drawdown_pct"
  | "num_trades"
  | "gamma"
  | "ent_coef"
  | "clip_range";

// Plain-English explanations for every column header, hyperparameter,
// metric, and feature flag on this page. Surfaced via the <Term> hover
// wrapper so a first-time visitor can self-discover what everything means
// without reading code or external docs.
const HELP = {
  // Run-level
  phase: "Phase 1 = TPE search (~5,200 trials searching the hyperparam space). Phase 2 = top configs retrained with multiple seeds to estimate seed variance.",
  ticker_filter: "Filter trials to a single stock symbol, or ALL for every ticker in this run.",

  // Per-ticker distribution
  ticker_col: "Stock symbol that was traded in this trial.",
  n: "Number of trials run for this ticker.",
  min: "Worst score this ticker saw across all its trials. (HIGHER is better — these are returns, so high = the agent made money.)",
  p25: "25th percentile of scores — 25% of trials scored at or below this value, 75% scored above. (HIGHER is better.)",
  median: "50th percentile — the middle score. Half above, half below. Less skewed by outliers than the mean. (HIGHER is better.)",
  p75: "75th percentile — 25% of trials scored at or above this value. (HIGHER is better.)",
  p90: "90th percentile — only the top 10% of trials beat this score. Good 'realistic best' metric. (HIGHER is better.)",
  max: "Best score this ticker achieved across all its trials. (HIGHER is better — but lucky outliers can sit here, so check median too.)",

  // Scatter section
  scatter_section: "Plots one dot per trial: x = the chosen hyperparameter's value, y = the trial's score (agent_mean_ret_pct, HIGHER is better). Helps spot which hyperparam values yield high scores — look for horizontal bands of dots near the top.",
  scatter_xaxis: "Which numeric hyperparameter to put on the x-axis. Try each to find the ones whose value actually moves the score.",

  // Trial-table columns
  trial: "Optuna's trial index for this ticker (0..trials_per_round-1, repeating across rounds).",
  round: "Phase 1 TPE round (0..num_rounds-1). Each round reuses prior rounds' results to refine the search.",
  score: "agent_mean_ret_pct — average % return the trained agent earned on the held-out test window. HIGHER = more profit. Positive = the agent beat sitting in cash; negative = it lost money.",
  cagr: "Compound Annual Growth Rate of the agent's equity curve. HIGHER = better. Reference points: SPY long-run ≈ 10%/yr, top hedge funds ≈ 15-20%/yr. >50% is suspiciously high — likely overfit.",
  mdd: "Maximum Drawdown — largest peak-to-trough % loss during the eval window. CLOSER TO 0 = better. Always negative or zero. -10% = small bad day; -50% = catastrophic.",
  num_trades: "Total buy + sell actions in the eval window. No 'right' answer: 0 = paralyzed agent (never traded); 100s = active swing; 1000s = scalper. Read alongside CAGR — a high-trade strategy needs high return to beat costs.",
  gamma_col: "PPO discount factor (γ). HIGH (≈0.99) = agent plans ~100 bars ahead; LOW (≈0.85) = short-sighted. No 'good' value — depends on the ticker; the search finds what works.",
  ent_coef_col: "Entropy coefficient — exploration bonus during training. HIGH = more random actions, slower convergence; LOW = quick convergence but risks local optima.",
  clip_col: "PPO clip range. HIGH (≈0.3) = aggressive policy updates (faster learning, less stable); LOW (≈0.1) = conservative updates (stable, slower).",
  flags_col: "Compact pills showing which feature/reward flags were ON for this trial. Green = on, gray = off. Hover each pill for its meaning.",
  features_col: "Exact feature column names fed to the PPO model after the flags + indicator preset were resolved.",
  config_key_col: "First 8 chars of the SHA1 of this trial's full (hyperparam + feature) fingerprint. Click to copy the full key — useful for grouping replicates of the identical config.",

  // Feature/reward flag pills (matches the FEATURE_FIELDS guidance)
  flag_vwap: "VWAP features — today's session Volume-Weighted Average Price + distance from it. Classic 'are we above or below fair value' signal.",
  flag_atr: "ATR (Average True Range) — Wilder-style volatility that includes intra-bar range and gaps.",
  flag_ret: "Lagged price returns (ret_1, ret_12, ret_48). The core momentum signal. Almost always on.",
  flag_vol: "Realized volatility — rolling stdev of returns over 12 / 48 bars.",
  flag_mkt: "Market-relative return: this ticker's return minus its benchmark's (e.g. AAPL minus SPY). Captures idiosyncratic moves.",
  flag_overnight: "Overnight-gap features — yesterday's close→today's open and yesterday's high-low range.",
  flag_sharpe: "Sharpe-window reward — replaces raw return with a rolling Sharpe-like ratio. Favors smooth equity curves.",
  flag_excess: "Excess-return reward — reward is portfolio return MINUS benchmark return. Strips beta credit; hunts true alpha.",

  // Numeric hyperparams in the scatter dropdown.
  // No "good/bad" — the right value depends on the ticker and is what TPE
  // is searching for. The notes describe what HIGH vs LOW values DO so
  // you can interpret the scatter (e.g. "scores cluster at high γ → this
  // ticker rewards long-horizon planning").
  hp_gamma: "Discount factor (γ). Search range 0.85–0.995. HIGH (≈0.99) = agent plans far ahead (~100-bar horizon); LOW (≈0.85) = short-sighted, optimizes near-term reward only. No universal 'best' — the search finds what works per ticker.",
  hp_gae_lambda: "GAE λ — bias/variance knob for advantage estimation. Search 0.90–0.99. HIGH (≈1.0) = pure Monte Carlo (low bias, high variance); LOW (≈0.0) = one-step TD (high bias, low variance). Neither is 'better' on its own.",
  hp_ent_coef: "Entropy coefficient — exploration bonus during training. Log-scaled search 0.005–0.1. HIGH = more random/exploratory actions, slower to converge; LOW = quick convergence but risks getting stuck in a local optimum.",
  hp_clip_range: "PPO clip range — how aggressively PPO clips the policy-update ratio per step. HIGH (≈0.3) = bigger policy updates → faster learning but more unstable; LOW (≈0.1) = small conservative updates → stable but slow.",
  hp_n_steps: "Rollout steps PPO collects before each policy update. HIGH = lower-variance gradient estimate (more stable training) but slower wall-clock; LOW = faster iterations but noisier gradients.",
  hp_batch_size: "Minibatch size for each PPO gradient step (must evenly divide n_steps). HIGH = smoother gradients per step but slower; LOW = noisier gradients per step but faster.",
  hp_sharpe_window: "Bar window for the rolling Sharpe reward (only used when use_sharpe_reward is on). HIGH = smoother, slower-reacting reward signal; LOW = twitchier, reacts to recent bars.",
  hp_max_hold_bars: "Hard ceiling — bars after which any open position is force-closed. HIGH = positions can ride long-term trends; LOW = forces frequent exits, suits scalping styles.",
};

const NUMERIC_HYPER_FIELDS: Array<{ key: keyof RunTrial; label: string; help: string }> = [
  { key: "gamma", label: "gamma", help: HELP.hp_gamma },
  { key: "gae_lambda", label: "gae_lambda", help: HELP.hp_gae_lambda },
  { key: "ent_coef", label: "ent_coef", help: HELP.hp_ent_coef },
  { key: "clip_range", label: "clip_range", help: HELP.hp_clip_range },
  { key: "n_steps", label: "n_steps", help: HELP.hp_n_steps },
  { key: "batch_size", label: "batch_size", help: HELP.hp_batch_size },
  { key: "sharpe_window", label: "sharpe_window", help: HELP.hp_sharpe_window },
  { key: "max_hold_bars", label: "max_hold_bars", help: HELP.hp_max_hold_bars },
];

/** Wraps a label in a dotted-underlined span with a native tooltip on
 *  hover. Used to make every header / abbreviation / pill self-explaining
 *  for first-time visitors. The dotted underline signals "hover me". */
function Term({ tip, children }: { tip: string; children: React.ReactNode }) {
  return (
    <span
      title={tip}
      style={{
        textDecoration: "underline dotted",
        textDecorationColor: "#475569",
        textUnderlineOffset: 3,
        cursor: "help",
      }}
    >
      {children}
    </span>
  );
}

function fmt(v: number | null | undefined, d = 3) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return Number(v).toFixed(d);
}

function flagPill(on: boolean, label: string, tip: string) {
  return (
    <span
      title={`${tip}\n\nValue: ${on ? "ON" : "off"}`}
      style={{
        background: on ? "#14532d" : "#374151",
        color: on ? "#86efac" : "#9ca3af",
        padding: "1px 5px", borderRadius: 3, fontSize: 10, fontWeight: 600, marginRight: 3,
        cursor: "help",
      }}
    >
      {label}
    </span>
  );
}

export function RunDetailPage() {
  const { runId } = useParams<{ runId: string }>();
  const [tickerFilter, setTickerFilter] = useState<string>("ALL");
  const [phase, setPhase] = useState<number>(1);
  const [sortBy, setSortBy] = useState<SortField>("agent_mean_ret_pct");
  const [sortDesc, setSortDesc] = useState(true);
  const [scatterField, setScatterField] = useState<keyof RunTrial>("gamma");
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Inline feedback for the per-row Promote button. Keyed by the row id
  // (ticker#config_key#replicate) so multiple rows can show their own
  // ok/error message simultaneously without polluting global state.
  const [promoteStatus, setPromoteStatus] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["run-detail", runId, phase, tickerFilter],
    queryFn: () => fetchRunDetail(runId!, {
      phase,
      ticker: tickerFilter === "ALL" ? undefined : tickerFilter,
    }),
    enabled: !!runId,
    staleTime: 120_000,
  });

  // Cross-reference: leaderboard tells us which (run_id#config_key) tuples
  // are already in TradingModels — used to render "promoted / pinned"
  // badges in the trial table so users don't double-promote. One query,
  // big page so we have a complete map regardless of ticker filter.
  const leaderboard = useQuery({
    queryKey: ["leaderboard-for-run-detail"],
    queryFn: () => fetchLeaderboard({ limit: 500 }),
    staleTime: 60_000,
  });
  const promotedMap = useMemo(() => {
    const m = new Map<string, ModelPerformance>();
    for (const row of leaderboard.data?.models ?? []) {
      // model_id is "{run_id}#{config_key}" — exactly what we need.
      if (row.model_id) m.set(row.model_id, row);
    }
    return m;
  }, [leaderboard.data]);

  const promoteMutation = useMutation({
    mutationFn: promoteTradingModel,
    onSuccess: (_resp, vars) => {
      const k = `${vars.ticker}#${vars.config_key}#${vars.replicate}`;
      setPromoteStatus((s) => ({ ...s, [k]: "✓ Promoted (pinned)" }));
      queryClient.invalidateQueries({ queryKey: ["leaderboard-for-run-detail"] });
    },
    onError: (err, vars) => {
      const k = `${vars.ticker}#${vars.config_key}#${vars.replicate}`;
      setPromoteStatus((s) => ({ ...s, [k]: `✗ ${(err as Error).message}` }));
    },
  });

  // Ticker dropdown options come from per_ticker stats (covers the FULL
  // set even when trials are server-side pruned).
  const tickers = useMemo(() => {
    if (!data) return [];
    return Object.keys(data.per_ticker).sort();
  }, [data]);

  // Client-side sort only (server already filtered by ticker).
  const filteredTrials = useMemo(() => {
    if (!data) return [];
    const sorted = [...data.trials].sort((a, b) => {
      const av = a[sortBy] as number | string | null;
      const bv = b[sortBy] as number | string | null;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return sortDesc ? bv - av : av - bv;
      }
      return sortDesc ? String(bv).localeCompare(String(av)) : String(av).localeCompare(String(bv));
    });
    return sorted;
  }, [data, sortBy, sortDesc]);

  // Scatter data: one point per trial with (hyperparam, score). When
  // ticker=ALL, color by ticker; otherwise just one color.
  const scatterPoints = useMemo(() => {
    const trials = filteredTrials;
    return trials
      .filter((t) => t[scatterField] !== null && t.agent_mean_ret_pct !== null)
      .map((t) => ({
        x: t[scatterField] as number,
        y: t.agent_mean_ret_pct as number,
        ticker: t.ticker,
        trial: t.trial_number,
      }));
  }, [filteredTrials, scatterField]);

  function toggleSort(field: SortField) {
    if (sortBy === field) {
      setSortDesc(!sortDesc);
    } else {
      setSortBy(field);
      setSortDesc(true);
    }
  }

  const sortArrow = (f: SortField) => (sortBy === f ? (sortDesc ? " ↓" : " ↑") : "");

  return (
    <div>
      <Link to="/runs/history" className="backlink">← Run history</Link>
      <h2 style={{ marginTop: 12 }}>Run detail</h2>
      <div style={{ color: "#94a3b8", fontSize: 12, fontFamily: "ui-monospace, monospace", marginBottom: 4 }}
           title="Unique run identifier — UTC timestamp the TPE run was kicked off.">
        {runId}
      </div>
      {data && (
        <div style={{ marginTop: 4, marginBottom: 4 }}>
          {(() => {
            const cfg = data.config as Record<string, unknown> | undefined;
            const label = cfg?.feature_config_label as string | undefined;
            const overrides = cfg?.feature_overrides as Record<string, unknown> | undefined;
            const pinCount = overrides ? Object.keys(overrides).length : 0;
            if (label) {
              return (
                <span
                  title={pinCount > 0
                    ? `${pinCount} pinned override(s) applied from this config. Hover keys in the trial table's Flags column to see which.`
                    : "Config selected but no overrides applied (config may be empty)."}
                  style={{
                    background: "#1e1b4b", color: "#c4b5fd",
                    border: "1px solid #4c1d95",
                    padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                  }}
                >
                  Config: <code style={{ color: "#e0e7ff" }}>{label}</code>
                  {pinCount > 0 && <span style={{ color: "#a78bfa", marginLeft: 6 }}>· {pinCount} pin{pinCount === 1 ? "" : "s"}</span>}
                </span>
              );
            }
            return (
              <span
                title="No named config was selected — Optuna searched the full default space for every hyperparameter."
                style={{
                  background: "#1e293b", color: "#94a3b8",
                  border: "1px solid #334155",
                  padding: "3px 10px", borderRadius: 12, fontSize: 12,
                }}
              >
                Config: <em>default search space (no pins)</em>
              </span>
            );
          })()}
        </div>
      )}
      <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 4, maxWidth: 880 }}>
        Every PPO training trial in this run with its score and key
        hyperparameters. Use the <Term tip={HELP.scatter_section}>scatter</Term> to see which
        hyperparam values led to high <Term tip={HELP.score}>scores</Term>, and the trial
        table to drill into individual trials. Hover any dotted-underlined label or column
        header to see what it means.
      </p>

      <div className="card" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ color: "#94a3b8", fontSize: 12 }}>
          <Term tip={HELP.phase}>Phase:</Term>{" "}
          <select value={phase} onChange={(e) => setPhase(Number(e.target.value))}
                  style={{ background: "#0b1220", color: "#cbd5e1", border: "1px solid #334155", padding: "4px 8px", borderRadius: 3 }}>
            <option value={1}>1 (search)</option>
            <option value={2}>2 (replicates)</option>
          </select>
        </label>
        <label style={{ color: "#94a3b8", fontSize: 12 }}>
          <Term tip={HELP.ticker_filter}>Ticker:</Term>{" "}
          <select value={tickerFilter} onChange={(e) => setTickerFilter(e.target.value)}
                  style={{ background: "#0b1220", color: "#cbd5e1", border: "1px solid #334155", padding: "4px 8px", borderRadius: 3 }}>
            <option value="ALL">ALL ({data?.trial_count_total ?? 0})</option>
            {tickers.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <span style={{ color: "#94a3b8", fontSize: 12 }}>
          {isLoading ? "loading…" : (
            data?.truncated
              ? `${data.trial_count_returned} shown (top ${data.per_ticker_n}/ticker; ${data.trial_count_total} total) — pick a ticker for full list`
              : `${data?.trial_count_returned ?? 0} trials shown`
          )}
        </span>
      </div>

      {error && <div className="card" style={{ color: "#fca5a5" }}>Error: {String(error)}</div>}

      {data && (
        <>
          {/* Per-ticker summary */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Per-ticker score distribution</h3>
            <div style={{ color: "#64748b", fontSize: 11, marginBottom: 8 }}>
              For each ticker, the spread of <Term tip={HELP.score}>scores</Term> across every trial in the selected phase.
              Wider spreads = the agent's performance was sensitive to hyperparam choices.
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th><Term tip={HELP.ticker_col}>Ticker</Term></th>
                    <th className="num"><Term tip={HELP.n}>N</Term></th>
                    <th className="num"><Term tip={HELP.min}>min</Term></th>
                    <th className="num"><Term tip={HELP.p25}>p25</Term></th>
                    <th className="num"><Term tip={HELP.median}>median</Term></th>
                    <th className="num"><Term tip={HELP.p75}>p75</Term></th>
                    <th className="num"><Term tip={HELP.p90}>p90</Term></th>
                    <th className="num"><Term tip={HELP.max}>max</Term></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.per_ticker).sort(([a], [b]) => a.localeCompare(b)).map(([t, s]) => (
                    <tr key={t}>
                      <td><code style={{ fontSize: 12 }}>{t}</code></td>
                      <td className="num">{s.count}</td>
                      <td className="num">{fmt(s.min)}</td>
                      <td className="num">{fmt(s.p25)}</td>
                      <td className="num" style={{ fontWeight: 600 }}>{fmt(s.median)}</td>
                      <td className="num">{fmt(s.p75)}</td>
                      <td className="num">{fmt(s.p90)}</td>
                      <td className="num" style={{ color: "#22c55e" }}>{fmt(s.max)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Hyperparam scatter */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>
              <Term tip={HELP.scatter_section}>Score vs hyperparameter</Term>
              <span style={{ color: "#94a3b8", fontSize: 12, fontWeight: 400, marginLeft: 8 }}>
                — each dot is one trial
              </span>
            </h3>
            <label style={{ color: "#94a3b8", fontSize: 12 }}>
              <Term tip={HELP.scatter_xaxis}>X-axis:</Term>{" "}
              <select value={String(scatterField)} onChange={(e) => setScatterField(e.target.value as keyof RunTrial)}
                      style={{ background: "#0b1220", color: "#cbd5e1", border: "1px solid #334155", padding: "4px 8px", borderRadius: 3 }}>
                {NUMERIC_HYPER_FIELDS.map((f) => (
                  <option key={String(f.key)} value={String(f.key)} title={f.help}>{f.label}</option>
                ))}
              </select>
              <span
                title={NUMERIC_HYPER_FIELDS.find((f) => f.key === scatterField)?.help ?? ""}
                style={{ color: "#94a3b8", fontSize: 11, marginLeft: 10, cursor: "help" }}
              >
                ⓘ what is {String(scatterField)}?
              </span>
            </label>
            <div style={{ width: "100%", height: 320, marginTop: 12 }}>
              <ResponsiveContainer>
                <ScatterChart margin={{ top: 8, right: 8, bottom: 28, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis type="number" dataKey="x" name={NUMERIC_HYPER_FIELDS.find(f => f.key === scatterField)?.label}
                         tick={{ fontSize: 11, fill: "#94a3b8" }} label={{ value: NUMERIC_HYPER_FIELDS.find(f => f.key === scatterField)?.label, position: "insideBottom", offset: -8, fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis type="number" dataKey="y" name="agent_mean_ret_pct"
                         tick={{ fontSize: 11, fill: "#94a3b8" }} label={{ value: "score", angle: -90, position: "insideLeft", fill: "#94a3b8", fontSize: 11 }} />
                  <ZAxis range={[40, 40]} />
                  <Tooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    contentStyle={{ background: "#0b1220", border: "1px solid #334155", fontSize: 12, color: "#e2e8f0" }}
                    itemStyle={{ color: "#e2e8f0" }}
                    labelStyle={{ color: "#e2e8f0" }}
                    formatter={(value: number | string, name: string) => [typeof value === "number" ? value.toFixed(4) : value, name]}
                    labelFormatter={() => ""}
                  />
                  <Scatter data={scatterPoints} fill="#7c3aed" fillOpacity={0.55} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
            <div style={{ color: "#64748b", fontSize: 11, marginTop: -4, fontStyle: "italic" }}>
              Vertical clusters = the value of this hyperparam doesn't strongly determine score (other params dominate).
              Diagonal trend = this hyperparam matters.
            </div>
          </div>

          {/* Trial table */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Trials</h3>
            <label style={{ color: "#94a3b8", fontSize: 12 }}>
              <input type="checkbox" checked={showAdvanced} onChange={() => setShowAdvanced(!showAdvanced)} />{" "}
              Show advanced columns (hyperparams + flags)
            </label>
            <div className="table-wrap" style={{ marginTop: 8 }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ cursor: "pointer" }} onClick={() => toggleSort("ticker")} title={HELP.ticker_col}>
                      Ticker{sortArrow("ticker")}
                    </th>
                    <th className="num" style={{ cursor: "pointer" }} onClick={() => toggleSort("trial_number")} title={HELP.trial}>
                      Trial{sortArrow("trial_number")}
                    </th>
                    <th className="num" title={HELP.round}>Round</th>
                    <th className="num" style={{ cursor: "pointer" }} onClick={() => toggleSort("agent_mean_ret_pct")} title={HELP.score}>
                      Score{sortArrow("agent_mean_ret_pct")}
                    </th>
                    <th className="num" style={{ cursor: "pointer" }} onClick={() => toggleSort("cagr_pct")} title={HELP.cagr}>
                      CAGR %{sortArrow("cagr_pct")}
                    </th>
                    <th className="num" style={{ cursor: "pointer" }} onClick={() => toggleSort("max_drawdown_pct")} title={HELP.mdd}>
                      MDD %{sortArrow("max_drawdown_pct")}
                    </th>
                    <th className="num" style={{ cursor: "pointer" }} onClick={() => toggleSort("num_trades")} title={HELP.num_trades}>
                      # trades{sortArrow("num_trades")}
                    </th>
                    <th title="Manually promote this trial to paper-live. Pins the ticker so the next auto-promote run won't overwrite your choice.">
                      Promote
                    </th>
                    {showAdvanced && (
                      <>
                        <th className="num" style={{ cursor: "pointer" }} onClick={() => toggleSort("gamma")} title={HELP.gamma_col}>γ{sortArrow("gamma")}</th>
                        <th className="num" style={{ cursor: "pointer" }} onClick={() => toggleSort("ent_coef")} title={HELP.ent_coef_col}>ent_coef{sortArrow("ent_coef")}</th>
                        <th className="num" style={{ cursor: "pointer" }} onClick={() => toggleSort("clip_range")} title={HELP.clip_col}>clip{sortArrow("clip_range")}</th>
                        <th title={HELP.flags_col}>Flags</th>
                        <th title={HELP.features_col}>Features (resolved)</th>
                        <th title={HELP.config_key_col}>config_key</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredTrials.map((t, idx) => (
                    <tr key={`${t.ticker}-${t.trial_number}-${t.replicate}-${idx}`}>
                      <td><code style={{ fontSize: 11 }}>{t.ticker}</code></td>
                      <td className="num">{t.trial_number ?? "—"}</td>
                      <td className="num">{t.tpe_round ?? "—"}</td>
                      <td className="num" style={{ fontWeight: 600,
                                                   color: (t.agent_mean_ret_pct ?? 0) > 0 ? "#22c55e" : "#ef4444" }}>
                        {fmt(t.agent_mean_ret_pct, 3)}
                      </td>
                      <td className="num">{fmt(t.cagr_pct, 2)}</td>
                      <td className="num" style={{ color: "#ef4444" }}>{fmt(t.max_drawdown_pct, 2)}</td>
                      <td className="num">{t.num_trades ?? "—"}</td>
                      <td style={{ fontSize: 11 }}>
                        {(() => {
                          const rowKey = `${t.ticker}#${t.config_key}#${t.replicate}`;
                          const status = promoteStatus[rowKey];
                          // model_id in TradingModels = "{run_id}#{config_key}"
                          // (without the per-replicate guid). Promoting any
                          // replicate of a config marks the whole model_id as
                          // "promoted" from the dashboard's perspective.
                          const modelId = `${runId}#${t.config_key}`;
                          const existing = promotedMap.get(modelId);
                          if (existing?.pinned) {
                            return (
                              <span title={`Pinned by manual promote. State=${existing.state ?? "?"}.`}
                                    style={{ color: "#a78bfa", fontWeight: 600 }}>
                                📌 pinned
                              </span>
                            );
                          }
                          if (existing) {
                            return (
                              <span title={`Already in TradingModels via auto-promote. State=${existing.state ?? "?"}.`}
                                    style={{ color: "#86efac" }}>
                                ✓ {existing.state ?? "live"}
                              </span>
                            );
                          }
                          if (status) {
                            return (
                              <span style={{ color: status.startsWith("✓") ? "#86efac" : "#fca5a5" }}>
                                {status}
                              </span>
                            );
                          }
                          const isPending = promoteMutation.isPending
                            && promoteMutation.variables?.config_key === t.config_key
                            && promoteMutation.variables?.replicate === t.replicate;
                          return (
                            <button
                              disabled={isPending || !runId}
                              onClick={() => {
                                const labelBits = [
                                  `Promote ${t.ticker} to paper-live?`,
                                  ``,
                                  `Config: ${t.config_key.slice(0, 12)}…`,
                                  `Replicate: ${t.replicate}`,
                                  `Score: ${fmt(t.agent_mean_ret_pct, 3)}`,
                                  `CAGR: ${fmt(t.cagr_pct, 2)}%`,
                                  `Max DD: ${fmt(t.max_drawdown_pct, 2)}%`,
                                  ``,
                                  `This will PIN ${t.ticker}. Future TPE runs won't auto-replace it until you unpin from the model detail page.`,
                                ];
                                if (!window.confirm(labelBits.join("\n"))) return;
                                promoteMutation.mutate({
                                  run_id: runId!,
                                  ticker: t.ticker,
                                  config_key: t.config_key,
                                  replicate: t.replicate,
                                });
                              }}
                              style={{
                                fontSize: 11, padding: "3px 8px",
                                background: isPending ? "#1e293b" : "#1e40af",
                                color: "#dbeafe", border: "1px solid #3b82f6",
                                borderRadius: 3, cursor: isPending ? "wait" : "pointer",
                                fontWeight: 600,
                              }}
                            >
                              {isPending ? "Promoting…" : "Promote"}
                            </button>
                          );
                        })()}
                      </td>
                      {showAdvanced && (
                        <>
                          <td className="num">{fmt(t.gamma, 4)}</td>
                          <td className="num">{fmt(t.ent_coef, 4)}</td>
                          <td className="num">{fmt(t.clip_range, 3)}</td>
                          <td style={{ fontSize: 10 }}>
                            {flagPill(t.use_vwap, "vwap", HELP.flag_vwap)}
                            {flagPill(t.use_atr, "atr", HELP.flag_atr)}
                            {flagPill(t.use_returns, "ret", HELP.flag_ret)}
                            {flagPill(t.use_volatility, "vol", HELP.flag_vol)}
                            {flagPill(t.use_market_relative, "mkt", HELP.flag_mkt)}
                            {flagPill(t.use_overnight, "o/n", HELP.flag_overnight)}
                            {flagPill(t.use_sharpe_reward, "shp", HELP.flag_sharpe)}
                            {flagPill(t.use_excess_reward, "exc", HELP.flag_excess)}
                          </td>
                          <td title={t.features.join(", ")}
                              style={{ fontSize: 11, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {t.features.length > 0
                              ? <code>{t.features.join(", ")}</code>
                              : <span style={{ color: "#64748b" }}>—</span>}
                          </td>
                          <td title={t.config_key} style={{ fontSize: 10 }}>
                            <code
                              style={{ cursor: "pointer", color: "#93c5fd" }}
                              onClick={() => {
                                navigator.clipboard?.writeText(t.config_key);
                              }}
                            >
                              {t.config_key.slice(0, 8) || "—"}
                            </code>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {data?.truncated && (
                <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 6, fontStyle: "italic" }}>
                  Showing top {data.per_ticker_n} trials per ticker by score
                  (Lambda response is capped at 6MB). Select a specific
                  ticker to see its full trial list ({data.trial_count_total} total in this run).
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
